// BILL-1: tell the open project page that a confirmed assistant action changed
// project data, so the page and its tabs refetch without a reload.
// Pure module: no "@/" imports, so jest can load it directly.
import { useEffect, useRef } from "react";

/** Window event fired after a confirmed assistant action changed a project. */
export const PROJECT_CHANGED_EVENT = "st:project-changed";

/** Dispatch PROJECT_CHANGED_EVENT on window with detail {projectId, tool}. */
export function emitProjectChanged(detail) {
  if (typeof window === "undefined") return;
  const d = detail || {};
  window.dispatchEvent(
    new CustomEvent(PROJECT_CHANGED_EVENT, {
      detail: { projectId: d.projectId || "", tool: d.tool || "" },
    }),
  );
}

/**
 * Call callback when a confirmed action changed this project (or when the
 * event names no project). The callback is read through a ref, so passing a
 * new function does not resubscribe. Unsubscribes on unmount.
 */
export function useProjectChanged(projectId, callback) {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onChanged = (e) => {
      const changedId = e?.detail?.projectId || "";
      if (!changedId || changedId === projectId) {
        const fn = callbackRef.current;
        if (typeof fn === "function") fn(e.detail);
      }
    };
    window.addEventListener(PROJECT_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(PROJECT_CHANGED_EVENT, onChanged);
  }, [projectId]);
}
